import { StorageService } from './StorageInterface';

/**
 * LocalStorage adapter implementing the StorageService interface
 * Uses batched updates for better performance on mobile devices
 */
export class LocalStorageAdapter implements StorageService {
  private pendingUpdates: Map<string, string> = new Map();
  private updateScheduled: boolean = false;
  private updateInterval: number = 1000; // 1 second default batch interval

  constructor(updateInterval?: number) {
    if (updateInterval) {
      this.updateInterval = updateInterval;
    }
  }

  async getItem(key: string): Promise<string | null> {
    // Check pending updates first
    if (this.pendingUpdates.has(key)) {
      return this.pendingUpdates.get(key) || null;
    }
    
    // Fall back to localStorage
    return localStorage.getItem(key);
  }
  
  async setItem(key: string, value: string): Promise<void> {
    // Add to pending updates
    this.pendingUpdates.set(key, value);
    
    // Schedule flush if not already scheduled
    this.scheduleFlush();
  }
  
  async removeItem(key: string): Promise<void> {
    // Remove from pending updates if present
    this.pendingUpdates.delete(key);
    
    // Remove from localStorage
    localStorage.removeItem(key);
  }
  
  async clear(): Promise<void> {
    // Clear pending updates
    this.pendingUpdates.clear();
    
    // Clear localStorage
    localStorage.clear();
  }
  
  async batchUpdate(updates: Array<{key: string, value: string}>): Promise<void> {
    // Add all updates to pending updates
    updates.forEach(update => {
      this.pendingUpdates.set(update.key, update.value);
    });
    
    // Schedule flush if not already scheduled
    this.scheduleFlush();
  }
  
  async flushUpdates(): Promise<void> {
    // Apply all pending updates to localStorage
    this.pendingUpdates.forEach((value, key) => {
      localStorage.setItem(key, value);
    });
    
    // Clear pending updates
    this.pendingUpdates.clear();
    
    // Reset scheduled flag
    this.updateScheduled = false;
  }
  
  private scheduleFlush(): void {
    if (this.updateScheduled) {
      return;
    }
    
    this.updateScheduled = true;
    
    // Schedule flush
    setTimeout(() => {
      this.flushUpdates();
    }, this.updateInterval);
  }
}
